// VoteControl — toggleable upvote button for posts and comments.
//
// Renders an upvote button that displays the current vote count and lets the
// authenticated user cast or retract a single vote with one click.
//
// Design source: design/project/scripts/ui.jsx vote control pattern.
// Toggle semantics match the API:
//   POST /api/v1/votes/posts/{post_id}    — toggle post vote
//   POST /api/v1/votes/comments/{comment_id} — toggle comment vote
//
// The component is optimistically updated: the UI flips immediately on click
// while the API call is in flight; it reverts on error.  This gives instant
// feedback on fast connections and degrades gracefully on slow ones.
//
// Usage:
//   <VoteControl target="post"    targetId="abc-123" initialCount={5} initialVoted={false} />
//   <VoteControl target="comment" targetId="42"      initialCount={2} initialVoted={true}  />

import { useCallback, useState } from "react";
import { apiClient } from "@/lib/api-client";

// ---------------------------------------------------------------------------
// Types (mirrors api/schemas/votes.py VoteResponse)
// ---------------------------------------------------------------------------

export type VoteTarget = "post" | "comment" | "training";

export interface VoteResponse {
  voted: boolean;
  target_type: string;
  target_id: string;
  vote_count: number;
}

// ---------------------------------------------------------------------------
// API helper
// ---------------------------------------------------------------------------

/**
 * Toggle the current user's vote on a post or comment.
 *
 * Returns the authoritative VoteResponse from the server.
 *
 * Requires an authenticated session cookie — the browser sends it
 * automatically. Throws ApiError on 401 (unauthenticated) or network errors.
 */
export async function toggleVote(
  target: VoteTarget,
  targetId: string,
): Promise<VoteResponse> {
  const pathByTarget: Record<VoteTarget, string> = {
    post: `/api/v1/votes/posts/${encodeURIComponent(targetId)}`,
    comment: `/api/v1/votes/comments/${encodeURIComponent(targetId)}`,
    training: `/api/v1/votes/training/${encodeURIComponent(targetId)}`,
  };
  const path = pathByTarget[target];

  return apiClient<VoteResponse>(path, { method: "POST" });
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface VoteControlProps {
  /** Whether this control votes on a post or a comment. */
  target: VoteTarget;
  /** The id of the post (string) or comment (number serialised to string). */
  targetId: string;
  /** Initial vote count — used as display until the first server response. */
  initialCount: number;
  /** Whether the current user has already voted on this item. */
  initialVoted: boolean;
  /**
   * Called after a successful toggle with the server-authoritative result.
   * Use this to propagate the live vote_count back up to a feed or detail view.
   */
  onVoteChange?: (result: VoteResponse) => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * VoteControl renders an upvote button with the current count.
 *
 * - Clicking the button toggles the vote via POST to the votes API.
 * - The UI updates optimistically on click and reverts on error.
 * - The button is disabled while a vote request is in flight to prevent
 *   rapid double-submit (the DB unique constraint handles concurrent races,
 *   but we surface a clean UX).
 *
 * Accessibility:
 * - aria-label reflects both the count and the current vote state.
 * - aria-pressed reflects the voted state for screen readers.
 * - data-testid="vote-btn" for test selectors.
 *
 * Design token / CSS class reference:
 *   .vc-btn             — outer button
 *   .vc-btn--voted      — modifier applied when voted=true
 *   .vc-btn--loading    — modifier applied while request is in flight
 *   .vc-icon            — upvote arrow icon wrapper
 *   .vc-count           — vote count number
 */
export function VoteControl({
  target,
  targetId,
  initialCount,
  initialVoted,
  onVoteChange,
}: VoteControlProps) {
  const [voted, setVoted] = useState(initialVoted);
  const [voteCount, setVoteCount] = useState(initialCount);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleVote = useCallback(async () => {
    if (loading) return;

    // Optimistic update
    const prevVoted = voted;
    const prevCount = voteCount;
    const nextVoted = !voted;
    const nextCount = voted ? voteCount - 1 : voteCount + 1;

    setVoted(nextVoted);
    setVoteCount(nextCount);
    setError(null);
    setLoading(true);

    try {
      const result = await toggleVote(target, targetId);
      // Reconcile with server-authoritative values
      setVoted(result.voted);
      setVoteCount(result.vote_count);
      onVoteChange?.(result);
    } catch (e: unknown) {
      // Revert optimistic update on error
      setVoted(prevVoted);
      setVoteCount(prevCount);
      const msg =
        e && typeof e === "object" && "status" in e && (e as { status: number }).status === 401
          ? "Sign in to vote"
          : "Could not save vote — try again";
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [loading, voted, voteCount, target, targetId, onVoteChange]);

  const label = voted
    ? `${voteCount} ${voteCount === 1 ? "upvote" : "upvotes"} — click to remove your vote`
    : `${voteCount} ${voteCount === 1 ? "upvote" : "upvotes"} — click to upvote`;

  return (
    <span className="vc-wrap" data-testid="vote-control">
      <button
        type="button"
        className={[
          "vc-btn",
          voted ? "vc-btn--voted" : "",
          loading ? "vc-btn--loading" : "",
        ]
          .filter(Boolean)
          .join(" ")}
        onClick={handleVote}
        disabled={loading}
        aria-label={label}
        aria-pressed={voted}
        data-testid="vote-btn"
        data-target-type={target}
        data-target-id={targetId}
      >
        {/* Upvote arrow — matches design/project/scripts/icons.jsx upvote pattern */}
        <span className="vc-icon" aria-hidden="true">
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill={voted ? "currentColor" : "none"}
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M12 19V5M5 12l7-7 7 7" />
          </svg>
        </span>
        <span className="vc-count" data-testid="vote-count">
          {voteCount}
        </span>
      </button>
      {error && (
        <span
          className="vc-error"
          role="alert"
          data-testid="vote-error"
        >
          {error}
        </span>
      )}
    </span>
  );
}

export default VoteControl;
